import {useCallback, useEffect, useRef, useState} from 'react'
import {
  useRetainBulkSelection,
  SelectionRestore,
  TableRow,
  TableDataItem,
} from './useRetainBulkSelection'

export interface UseBulkSelectionSyncParams {
  tableData: TableDataItem[]
  searchParams: URLSearchParams
}

export interface UseBulkSelectionSyncReturn {
  selectionToRestore: SelectionRestore | null
  triggerBulkReload: (selectedRows: TableRow[]) => void
  clearRetainedSelection: () => void
}

export const useBulkSelectionSync = ({
  tableData,
  searchParams,
}: UseBulkSelectionSyncParams): UseBulkSelectionSyncReturn => {
  const {
    isBulkActionReload,
    captureSelection,
    clearRetainedSelection: retainClear,
    reconcileAfterReload,
    acknowledgeRestore,
  } = useRetainBulkSelection()

  const [selectionToRestore, setSelectionToRestore] = useState<SelectionRestore | null>(null)
  const prevSearchParamsRef = useRef(searchParams.toString())

  // Captures the current selection IDs immediately so Table can skip the
  // pageDisabled reset while the reload is in flight.
  const triggerBulkReload = useCallback(
    (selectedRows: TableRow[]): void => {
      const capturedIds = captureSelection(selectedRows)
      setSelectionToRestore({ids: capturedIds})
    },
    [captureSelection]
  )

  // Resets both the retained IDs and the local selectionToRestore state.
  const clearRetainedSelection = useCallback((): void => {
    retainClear()
    setSelectionToRestore(null)
  }, [retainClear])

  // Reconciliation effect: refines the restore after new tableData arrives.
  // Runs only when isBulkActionReload is true (i.e. a bulk-action reload is pending).
  useEffect(() => {
    if (!isBulkActionReload) return
    const restoredIds = reconcileAfterReload(tableData)
    setSelectionToRestore({ids: restoredIds})
    acknowledgeRestore()
  }, [tableData, isBulkActionReload, reconcileAfterReload, acknowledgeRestore])

  // Search-params guard: clears retained selection on filter/tab/pagination changes.
  // Intentionally omits isBulkActionReload from deps — reads it as a stale-closure
  // guard that is only meaningful when searchParams actually changes.
  useEffect(() => {
    const current = searchParams.toString()
    if (current !== prevSearchParamsRef.current && !isBulkActionReload) {
      clearRetainedSelection()
    }
    prevSearchParamsRef.current = current
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    selectionToRestore,
    triggerBulkReload,
    clearRetainedSelection,
  }
}
